import React, { useEffect, useRef } from 'react';

import { BlankInfo } from '../MemoryGameMainGroup';

import styles from './GameQuestionPanel.module.css';
import { QuickMemoryBlankUnit, SpellingBlankUnit } from '../../Molecules';

type Unit = {
  key: string;
  type: 'hint' | 'blank';
  distractors?: 'none' | 'auto' | string[];
};

type Word = {
  word: string;
  units: Unit[];
};

export type Answer = {
  words: Word[];
  soundMode: string;
  locale: string;
  answerFirstLetter: boolean;
  isLyrics: boolean;
};

export enum TextOrderingGameMode {
  QuickMemory = 'QuickMemory',
  Syllable = 'Syllable',
  Spelling = 'Spelling',
}

type GameQuestionPanelProps = {
  answer: Answer;
  currentBlankIndex: number;
  blankInfos: Record<number, BlankInfo>;
  mode: TextOrderingGameMode;
};

export const GameQuestionPanel: React.FC<GameQuestionPanelProps> = ({
  answer,
  currentBlankIndex = 0,
  blankInfos,
  mode,
}) => {
  const blankRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const currentRef = blankRefs.current[currentBlankIndex];
    if (currentRef) {
      currentRef.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentBlankIndex]);

  const calculateHintIndex = (length: number, unitsLength: number) => {
    if (length <= 0) return Math.max(0, length - 1);
    const index = unitsLength % length;
    return index === 0 ? 1 : index;
  };

  const allBlankIndices = answer.words.flatMap(
    (word, wordIndex) =>
      word.units
        .map((unit, idx) =>
          unit.type === 'blank' ? { wordIndex, unitIndex: idx } : null,
        )
        .filter((item) => item !== null) as {
        wordIndex: number;
        unitIndex: number;
      }[],
  );

  return (
    <div className={styles.container}>
      {answer.words.map((word, wordIndex) => (
        <div
          key={wordIndex}
          className={
            mode === TextOrderingGameMode.QuickMemory
              ? styles.quickMemoryWordsContainer
              : styles.spellingWordsContainer
          }
        >
          {word.units.map((unit, idx) => {
            if (unit.type === 'hint') {
              return (
                <span key={idx} className={styles.hint}>
                  {unit.key}
                </span>
              );
            } else if (unit.type === 'blank') {
              const length = unit.key.length;
              const hintIndex = calculateHintIndex(
                length,
                word.units.filter((item) => item.key !== ' ').length,
              );

              const globalBlankIndex = allBlankIndices.findIndex(
                (item) =>
                  item.wordIndex === wordIndex && item.unitIndex === idx,
              );
              const isHighlighted = globalBlankIndex === currentBlankIndex;
              const isAnswered =
                blankInfos[globalBlankIndex]?.pickedAnswer != undefined;
              const score = blankInfos[globalBlankIndex]?.score ?? 100;

              return mode === TextOrderingGameMode.QuickMemory ? (
                <QuickMemoryBlankUnit
                  key={idx}
                  unit={unit}
                  isAnswered={isAnswered}
                  isHighlighted={isHighlighted}
                  hintIndex={hintIndex}
                  score={score}
                  globalBlankIndex={globalBlankIndex}
                  blankRefs={blankRefs}
                />
              ) : (
                <SpellingBlankUnit
                  key={idx}
                  unit={unit}
                  isAnswered={isAnswered}
                  isHighlighted={isHighlighted}
                  hintIndex={hintIndex}
                  score={score}
                  globalBlankIndex={globalBlankIndex}
                  blankRefs={blankRefs}
                  variant={mode}
                />
              );
            }
            return null;
          })}
        </div>
      ))}
    </div>
  );
};
